import {
  alpha,
  Box,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import type { Theme } from '@mui/material';
import { Control, Controller, FieldValues } from 'react-hook-form';
import React from 'react';

export interface ToggleField {
  id: string;
  label?: string;
  name?: string;
  required?: boolean;
  control: Control<FieldValues, any>;
  buttons: { label: string; value: unknown }[];
}

export const ToggleField = ({
  id,
  name,
  required = false,
  buttons,
  control,
  label,
}: ToggleField) => {
  return (
    <Controller
      rules={{ required }}
      control={control}
      name={name ?? id}
      render={({ field }) => {
        return (
          <ToggleButtonGroup
            {...field}
            aria-labelledby="group"
            value={field.value}
            exclusive
            sx={{
              paddingLeft: 0,
              marginLeft: 0,
              marginTop: '5px',
            }}
          >
            <Stack>
              <Box
                component="p"
                sx={{
                  fontSize: '16px',
                  // text.secondary === the original rgba(0,0,0,0.6) in light and
                  // legible in dark. Box (not Typography) keeps the plain <p>
                  // metrics — no body1 line-height/letter-spacing drift.
                  color: 'text.secondary',
                  padding: '5px 0',
                  margin: 0,
                }}
              >
                {label}
              </Box>
              <Stack direction="row">
                {buttons?.map(
                  (
                    {
                      label: buttonLabel,
                      value,
                    }: { label: string | React.ReactElement; value: unknown },
                    index: number
                  ) => {
                    return (
                      <ToggleButton
                        key={index}
                        value={String(value)}
                        selected={String(field.value) === String(value)}
                        sx={styles(index, buttons.length)}
                        onChange={(
                          event: React.MouseEvent<HTMLElement>,
                          buttonValue: string
                        ) => {
                          if (String(buttonValue) === String(field.value)) {
                            return field.onChange(null);
                          }

                          return field.onChange(buttonValue);
                        }}
                      >
                        {buttonLabel}
                      </ToggleButton>
                    );
                  }
                )}
              </Stack>
            </Stack>
          </ToggleButtonGroup>
        );
      }}
    />
  );
};

const selectedLook = {
  color: 'action.active',
  backgroundColor: '#EBEBEB',
  '&:hover': {
    backgroundColor: (theme: Theme) =>
      alpha(theme.palette.text.primary, theme.palette.action.hoverOpacity),
    '@media (hover: none)': {
      backgroundColor: 'transparent',
    },
  },
};

const styles = (index: number, length: number) => {
  return {
    '&.Mui-selected': selectedLook,
    borderBottomRightRadius:
      index === 0 ? 0 : index !== 0 && index !== length - 1 ? 0 : 4,
    borderTopRightRadius:
      index === 0 ? 0 : index !== 0 && index !== length - 1 ? 0 : 4,
    borderBottomLeftRadius:
      index === length - 1 ? 0 : index !== length - 1 && index !== 0 ? 0 : 4,
    borderTopLeftRadius:
      index === length - 1 ? 0 : index !== length - 1 && index !== 0 ? 0 : 4,
  };
};
